import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { resolveImageUrl } from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Small self-fetching card for a "featured_pillar" block.
 * If `pillar_id` is empty, falls back to whatever /api/pillars/featured returns
 * (which itself honours the site-wide override or the monthly rotation).
 */
function FeaturedPillarBlock({ heading, pillarId }) {
    const [pillar, setPillar] = useState(null);
    useEffect(() => {
        const url = pillarId ? `${API_URL}/api/pillars/${pillarId}` : `${API_URL}/api/pillars/featured`;
        axios.get(url).then(r => setPillar(r.data)).catch(() => setPillar(null));
    }, [pillarId]);
    if (!pillar) return null;
    return (
        <section className="py-20 sm:py-24 bg-white border-y border-[#E5E0D8]" data-testid="home-block-featured-pillar">
            <div className="max-w-5xl mx-auto px-6 sm:px-12">
                <div className="text-center mb-12">
                    <p className="overline mb-3">Mirror Circle Partnership</p>
                    <h2 className="heading-2">{heading || 'Meet our pillar'}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-center">
                    <div className="aspect-square bg-gradient-to-br from-[#5B2D8E]/10 to-[#D8B4E2]/20 rounded-sm overflow-hidden flex items-center justify-center">
                        {pillar.profile_image_url ? (
                            <img src={resolveImageUrl(pillar.profile_image_url)} alt={pillar.name} className="w-full h-full object-cover" />
                        ) : (
                            <span className="font-serif text-7xl text-[#5B2D8E]">{(pillar.name || '?').charAt(0)}</span>
                        )}
                    </div>
                    <div className="md:col-span-2">
                        <h3 className="font-serif text-3xl text-[#1A1A2E] mb-2">{pillar.name}</h3>
                        {pillar.specialty && (
                            <p className="text-xs uppercase tracking-widest text-[#5B2D8E] mb-4">{pillar.specialty}</p>
                        )}
                        {(pillar.city || pillar.country) && (
                            <p className="text-sm text-[#4A4A68] mb-4">
                                {[pillar.city, pillar.country].filter(Boolean).join(', ')}
                            </p>
                        )}
                        {pillar.bio && (
                            <p className="text-[#1A1A2E] mb-6 whitespace-pre-wrap line-clamp-5">{pillar.bio}</p>
                        )}
                        <Link to={`/pillars/${pillar.id}`} className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#5B2D8E] hover:gap-3 transition-all">
                            View full profile <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

/**
 * Renders a list of homepage blocks configured via the admin Homepage Builder.
 * Each block has: { type, props }
 * Supported types: hero, text, image, cta, quote, two_column, featured_pillar
 * Body-like fields (`body`, `left_body`) may contain HTML from TipTap.
 */
export default function HomeBlockRenderer({ blocks }) {
    if (!blocks || blocks.length === 0) return null;

    const isHtml = (v) => typeof v === 'string' && v.trim().startsWith('<');
    const RichBody = ({ value, className }) =>
        isHtml(value)
            ? <div className={`prose max-w-none tiptap-view ${className || ''}`} dangerouslySetInnerHTML={{ __html: value }} />
            : <div className={`whitespace-pre-wrap ${className || ''}`}>{value}</div>;

    return (
        <>
            {blocks.map((b, i) => {
                const p = b.props || {};
                const key = b.id || `${b.type}-${i}`;
                switch (b.type) {
                    case 'hero':
                        return (
                            <section key={key} className="relative py-24 sm:py-32 md:py-40 bg-[#1A1A2E] text-[#F9F6F0] overflow-hidden" data-testid={`home-block-hero-${i}`}>
                                {p.image_url && (
                                    <div className="absolute inset-0">
                                        <img src={resolveImageUrl(p.image_url)} alt="" className="w-full h-full object-cover opacity-60" />
                                        <div className="absolute inset-0 bg-[#1A1A2E]/50" />
                                    </div>
                                )}
                                <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-12 text-center">
                                    {p.heading && <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light mb-6">{p.heading}</h2>}
                                    {p.subheading && <p className="font-sans text-lg sm:text-xl text-[#F9F6F0]/90 max-w-2xl mx-auto mb-8">{p.subheading}</p>}
                                    {p.cta_label && p.cta_href && (
                                        <a href={p.cta_href} className="inline-block bg-[#F9F6F0] text-[#5B2D8E] px-8 py-4 rounded-sm font-sans text-sm uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#1A1A2E] transition">
                                            {p.cta_label}
                                        </a>
                                    )}
                                </div>
                            </section>
                        );

                    case 'text':
                        return (
                            <section key={key} className="py-16 sm:py-20 bg-[#F9F6F0]" data-testid={`home-block-text-${i}`}>
                                <div className="max-w-3xl mx-auto px-6 sm:px-12" style={{ textAlign: p.align || 'left' }}>
                                    {p.heading && <h2 className="heading-2 mb-6">{p.heading}</h2>}
                                    {p.body && <RichBody value={p.body} className="text-[#1A1A2E]" />}
                                </div>
                            </section>
                        );

                    case 'image': {
                        const img = (
                            <figure className="max-w-5xl mx-auto px-6 sm:px-12">
                                {p.image_url && (
                                    <img src={resolveImageUrl(p.image_url)} alt={p.caption || ''} className="w-full rounded-sm shadow-md" />
                                )}
                                {p.caption && (
                                    <figcaption className="text-center text-sm text-[#4A4A68] mt-3 italic">{p.caption}</figcaption>
                                )}
                            </figure>
                        );
                        return (
                            <section key={key} className="py-16 bg-[#F9F6F0]" data-testid={`home-block-image-${i}`}>
                                {p.href ? <a href={p.href} className="block">{img}</a> : img}
                            </section>
                        );
                    }

                    case 'cta':
                        return (
                            <section key={key} className="py-20 bg-[#5B2D8E] text-[#F9F6F0]" data-testid={`home-block-cta-${i}`}>
                                <div className="max-w-3xl mx-auto px-6 sm:px-12 text-center">
                                    {p.heading && <h2 className="font-serif text-3xl sm:text-4xl mb-4">{p.heading}</h2>}
                                    {p.body && <RichBody value={p.body} className="text-[#F9F6F0]/90 mb-8" />}
                                    {p.button_label && p.button_href && (
                                        <a href={p.button_href} className="inline-block bg-[#F9F6F0] text-[#5B2D8E] px-8 py-4 rounded-sm font-sans text-sm uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#1A1A2E] transition">
                                            {p.button_label}
                                        </a>
                                    )}
                                </div>
                            </section>
                        );

                    case 'quote':
                        return (
                            <section key={key} className="py-20 bg-[#F2EBE5]" data-testid={`home-block-quote-${i}`}>
                                <div className="max-w-3xl mx-auto px-6 sm:px-12 text-center">
                                    <p className="font-serif text-2xl sm:text-3xl md:text-4xl italic text-[#1A1A2E] leading-relaxed">
                                        “{p.quote}”
                                    </p>
                                    {p.author && (
                                        <p className="mt-6 uppercase tracking-widest text-xs text-[#4A4A68]">— {p.author}</p>
                                    )}
                                </div>
                            </section>
                        );

                    case 'two_column': {
                        const text = (
                            <div>
                                {p.left_heading && <h2 className="heading-2 mb-6">{p.left_heading}</h2>}
                                {p.left_body && <RichBody value={p.left_body} className="text-[#1A1A2E]" />}
                            </div>
                        );
                        const img = p.right_image_url ? (
                            <div className="relative">
                                <img src={resolveImageUrl(p.right_image_url)} alt="" className="w-full rounded-sm shadow-xl" />
                            </div>
                        ) : null;
                        return (
                            <section key={key} className="py-20 bg-[#F9F6F0]" data-testid={`home-block-two-${i}`}>
                                <div className="max-w-7xl mx-auto px-6 sm:px-12">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                                        {p.flip ? <>{img}{text}</> : <>{text}{img}</>}
                                    </div>
                                </div>
                            </section>
                        );
                    }

                    case 'featured_pillar':
                        return (
                            <FeaturedPillarBlock
                                key={key}
                                heading={p.heading}
                                pillarId={p.pillar_id || ''}
                            />
                        );

                    default:
                        return null;
                }
            })}
        </>
    );
}
